/**
 * Universal date utility functions for visa calculations
 * Respects user preferences and browser locale settings
 */

import { getCountryTimezone } from './timezoneUtils';

// Cache for performance optimization
let cachedLocale = null;
let cachedTimezone = null;

// Get user's saved preferences or fallback to browser defaults
export const getUserPreferences = () => {
  try {
    const saved = localStorage.getItem('userPreferences');
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.warn('Error loading user preferences:', error);
    return null;
  }
};

// Get user's browser locale and timezone (cached for performance)
export const getUserLocale = () => {
  if (!cachedLocale) {
    cachedLocale = navigator.language || navigator.languages[0] || 'en-US';
  }
  return cachedLocale;
};

export const getUserTimezone = () => {
  if (!cachedTimezone) {
    cachedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  return cachedTimezone;
};

// Cache for browser format detection
let cachedBrowserDateFormat = null;
let cachedBrowserTimeFormat = null;

/**
 * Detect the browser's actual date format pattern
 * Uses Intl.DateTimeFormat to get the real format, not just locale guessing
 * @returns {string} 'us' (MM/DD/YYYY), 'eu' (DD.MM.YYYY), or 'uk' (DD/MM/YYYY)
 */
export const detectBrowserDateFormat = () => {
  if (cachedBrowserDateFormat) {
    return cachedBrowserDateFormat;
  }

  try {
    const locale = getUserLocale();
    const testDate = new Date(2025, 8, 21); // September 21, 2025 (month is 0-indexed)
    
    // Get the formatted date using browser's locale settings
    const formatter = new Intl.DateTimeFormat(locale, {
      dateStyle: 'short'
    });
    const formatted = formatter.format(testDate);
    
    // Analyze the format pattern
    // Format will be something like: "9/21/2025", "21.09.2025", "21/09/2025", etc.
    
    // Check for MM/DD/YYYY pattern (US format)
    // Pattern: month/day/year where month comes first
    const parts = formatted.split(/[.\/\s-]/);
    if (parts.length >= 3) {
      const firstPart = parseInt(parts[0], 10);
      const secondPart = parseInt(parts[1], 10);
      
      // If first part is month (1-12) and second is day (1-31), it's US format
      if (firstPart <= 12 && secondPart <= 31 && firstPart !== secondPart) {
        // Additional check: if first part is clearly a month (1-12) and second is day
        if (firstPart <= 12) {
          cachedBrowserDateFormat = 'us';
          return 'us';
        }
      }
      
      // Check for DD.MM.YYYY pattern (EU format with dots)
      if (formatted.includes('.')) {
        // If first part is day (1-31) and second is month (1-12), it's EU format
        if (firstPart <= 31 && secondPart <= 12 && firstPart !== secondPart) {
          cachedBrowserDateFormat = 'eu';
          return 'eu';
        }
      }
      
      // Check for DD/MM/YYYY pattern (UK format)
      if (formatted.includes('/')) {
        // If first part is day (1-31) and second is month (1-12), it's UK format
        if (firstPart <= 31 && secondPart <= 12 && firstPart !== secondPart) {
          cachedBrowserDateFormat = 'uk';
          return 'uk';
        }
      }
    }
    
    // Fallback: use a more reliable method - check the actual pattern
    // Create formatters for each locale and see which matches
    const usFormatter = new Intl.DateTimeFormat('en-US', { dateStyle: 'short' });
    const euFormatter = new Intl.DateTimeFormat('de-DE', { dateStyle: 'short' });
    const ukFormatter = new Intl.DateTimeFormat('en-GB', { dateStyle: 'short' });
    
    const usFormatted = usFormatter.format(testDate);
    const euFormatted = euFormatter.format(testDate);
    const ukFormatted = ukFormatter.format(testDate);
    
    // Check which format matches the browser's format
    const browserFormatted = formatter.format(testDate);
    
    // Compare patterns (normalize separators for comparison)
    const normalize = (str) => str.replace(/[.\/\s-]/g, '');
    const browserNormalized = normalize(browserFormatted);
    
    if (normalize(usFormatted) === browserNormalized) {
      cachedBrowserDateFormat = 'us';
      return 'us';
    } else if (normalize(euFormatted) === browserNormalized) {
      cachedBrowserDateFormat = 'eu';
      return 'eu';
    } else if (normalize(ukFormatted) === browserNormalized) {
      cachedBrowserDateFormat = 'uk';
      return 'uk';
    }
    
    // Final fallback: check locale string
    if (locale.startsWith('en-US')) {
      cachedBrowserDateFormat = 'us';
      return 'us';
    } else if (locale.startsWith('en-GB')) {
      cachedBrowserDateFormat = 'uk';
      return 'uk';
    } else if (locale.startsWith('de') || locale.startsWith('fr') || locale.startsWith('it') || locale.startsWith('es')) {
      cachedBrowserDateFormat = 'eu';
      return 'eu';
    }
    
    // Default fallback
    cachedBrowserDateFormat = 'us';
    return 'us';
  } catch (error) {
    console.warn('Error detecting browser date format:', error);
    // Fallback to locale-based detection
    const locale = getUserLocale();
    if (locale.startsWith('en-US')) return 'us';
    if (locale.startsWith('en-GB')) return 'uk';
    if (locale.startsWith('de') || locale.startsWith('fr') || locale.startsWith('it') || locale.startsWith('es')) return 'eu';
    return 'us';
  }
};

/**
 * Detect the browser's time format preference (12h vs 24h)
 * @returns {boolean} true for 12-hour format (AM/PM), false for 24-hour format
 */
export const detectBrowserTimeFormat = () => {
  if (cachedBrowserTimeFormat !== null) {
    return cachedBrowserTimeFormat;
  }

  try {
    const locale = getUserLocale();
    const testTime = new Date(2025, 0, 1, 15, 30); // 3:30 PM
    
    // Get resolved options to check hourCycle or hour12
    const formatter = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    const resolvedOptions = formatter.resolvedOptions();
    
    // Check hourCycle first (preferred method)
    if (resolvedOptions.hourCycle) {
      const is12Hour = resolvedOptions.hourCycle === 'h11' || resolvedOptions.hourCycle === 'h12';
      cachedBrowserTimeFormat = is12Hour;
      return is12Hour;
    }
    
    // Fallback to hour12 property
    if (resolvedOptions.hour12 !== undefined) {
      cachedBrowserTimeFormat = resolvedOptions.hour12;
      return resolvedOptions.hour12;
    }
    
    // Fallback: check the formatted output
    const formatted = formatter.format(testTime);
    // If it contains AM/PM or a/pm, it's 12-hour format
    const is12Hour = /[ap]m/i.test(formatted);
    cachedBrowserTimeFormat = is12Hour;
    return is12Hour;
  } catch (error) {
    console.warn('Error detecting browser time format:', error);
    // Fallback: most locales use 24h except US/UK
    const locale = getUserLocale();
    const is12Hour = locale.startsWith('en-US') || locale.startsWith('en-GB');
    cachedBrowserTimeFormat = is12Hour;
    return is12Hour;
  }
};

/**
 * Get comprehensive browser format information
 * @returns {Object} Object containing dateFormat, timeFormat, and display info
 */
export const getBrowserFormatInfo = () => {
  const dateFormat = detectBrowserDateFormat();
  const timeFormat = detectBrowserTimeFormat();
  const locale = getUserLocale();
  const timezone = getUserTimezone();
  
  // Get example date and time
  const testDate = new Date(2025, 8, 21, 15, 30); // September 21, 2025, 3:30 PM
  
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    dateStyle: 'short'
  });
  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: timeFormat
  });
  
  return {
    dateFormat, // 'us', 'eu', or 'uk'
    timeFormat, // true for 12h, false for 24h
    locale,
    timezone,
    dateExample: dateFormatter.format(testDate),
    timeExample: timeFormatter.format(testDate),
    datePattern: dateFormat === 'us' ? 'MM/DD/YYYY' : dateFormat === 'eu' ? 'DD.MM.YYYY' : 'DD/MM/YYYY',
    timePattern: timeFormat ? '12h (AM/PM)' : '24h'
  };
};

// Get the effective date format based on user preferences
export const getEffectiveDateFormat = () => {
  const preferences = getUserPreferences();
  const dateFormat = preferences?.dateFormat || 'auto';

  if (dateFormat === 'auto') {
    // Use browser's actual date format detection instead of locale guessing
    return detectBrowserDateFormat();
  }

  return dateFormat;
};

// Get the effective time format based on user preferences
export const getEffectiveTimeFormat = () => {
  const preferences = getUserPreferences();
  const timeFormat = preferences?.timeFormat || 'auto';
  return timeFormat; // Returns 'auto', '12h', or '24h'
};

// Get the effective time format as a boolean for use in Intl.DateTimeFormat
// Returns true for 12-hour format (AM/PM), false for 24-hour format
export const getEffectiveTimeFormatBoolean = () => {
  const timeFormat = getEffectiveTimeFormat();
  
  if (timeFormat === '12h') {
    return true; // 12-hour format with AM/PM
  } else if (timeFormat === '24h') {
    return false; // 24-hour format
  } else {
    // 'auto' - use browser detection
    return detectBrowserTimeFormat();
  }
};

// Format date according to user preferences
export const formatDate = (date, options = {}) => {
  if (!date) return '';

  const dateObj = new Date(date);
  const format = getEffectiveDateFormat();

  // If user wants full format (with weekday, month name)
  if (options.full !== false) {
    const userLocale = getUserLocale();
    const fullOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      ...options
    };
    return dateObj.toLocaleDateString(userLocale, fullOptions);
  }

  // Short format based on user preference
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();

  switch (format) {
    case 'us':
      return `${month}/${day}/${year}`;
    case 'eu':
      return `${day}.${month}.${year}`;
    case 'uk':
      return `${day}/${month}/${year}`;
    default:
      return `${month}/${day}/${year}`;
  }
};

// Format short date for input fields and compact displays
export const formatShortDate = (date) => {
  return formatDate(date, { full: false });
};

// Get format label and pattern for display
export const getDateFormatLabel = () => {
  const format = getEffectiveDateFormat();

  switch (format) {
    case 'us':
      return { label: 'US', pattern: 'MM/DD/YYYY' };
    case 'eu':
      return { label: 'EU', pattern: 'DD.MM.YYYY' };
    case 'uk':
      return { label: 'UK', pattern: 'DD/MM/YYYY' };
    default:
      return { label: 'US', pattern: 'MM/DD/YYYY' };
  }
};

// Format date for calendar display
export const formatCalendarDate = (date) => {
  if (!date) return '';

  const dateObj = new Date(date);
  const format = getEffectiveDateFormat();

  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();

  switch (format) {
    case 'us':
      return `${month}/${day}/${year}`;
    case 'eu':
      return `${day}.${month}.${year}`;
    case 'uk':
      return `${day}/${month}/${year}`;
    default:
      return `${month}/${day}/${year}`;
  }
};

// Format date with format indicator
export const formatCalendarDateWithLabel = (date) => {
  if (!date) return '';

  const formattedDate = formatCalendarDate(date);
  const { label, pattern } = getDateFormatLabel();

  return `${formattedDate} - ${label} • ${pattern}`;
};

// Format expiration date with time - FIXED VERSION
export const formatExpirationDate = (date, country = null) => {
  const userLocale = getUserLocale();

  // Use country timezone if provided, otherwise fall back to user timezone
  let timezone;
  if (country) {
    // getCountryTimezone is imported at top level to avoid circular dependency issues
    const countryTz = getCountryTimezone(country);
    timezone = countryTz?.timezone || getUserTimezone();
  } else {
    timezone = getUserTimezone();
  }

  // THE CORRECT SOLUTION: 
  // Don't try to convert timezones - just format the date we have and force the time display
  if (country) {
    // Get the date parts from the input date
    const inputDate = new Date(date);

    // Format the date manually to ensure we show exactly what we want
    const dateStr = inputDate.toLocaleDateString(userLocale, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: timezone
    });

    // Always append 23:59:59 - this is what we want to show regardless of timezone conversion
    return `${dateStr} at 23:59:59`;
  }

  // Fallback for no country
  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: timezone,
  };
  return new Date(date).toLocaleDateString(userLocale, options);
};

// Get date format example for current user preference
export const getDateFormatExample = () => {
  const format = getEffectiveDateFormat();
  const today = new Date();

  switch (format) {
    case 'us':
      return today.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    case 'eu':
      return formatShortDate(today).replace(/\//g, '.');
    case 'uk':
      return today.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    default:
      return today.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  }
};

// Parse date string in user's preferred format
export const parseUserDate = (dateString) => {
  if (!dateString) return null;

  const format = getEffectiveDateFormat();
  let day, month, year;

  switch (format) {
    case 'us':
      // MM/DD/YYYY
      const usParts = dateString.split('/');
      if (usParts.length === 3) {
        [month, day, year] = usParts.map(p => parseInt(p, 10));
      }
      break;
    case 'eu':
      // DD.MM.YYYY
      const euParts = dateString.split('.');
      if (euParts.length === 3) {
        [day, month, year] = euParts.map(p => parseInt(p, 10));
      }
      break;
    case 'uk':
      // DD/MM/YYYY
      const ukParts = dateString.split('/');
      if (ukParts.length === 3) {
        [day, month, year] = ukParts.map(p => parseInt(p, 10));
      }
      break;
    default:
      return new Date(dateString);
  }

  if (day && month && year) {
    return new Date(year, month - 1, day); // month is 0-indexed
  }

  return new Date(dateString); // fallback
};

export const calculateExpirationDate = (selectedDate, visaDuration) => {
  if (!selectedDate) return new Date();
  const [year, month, day] = selectedDate
    .split("-")
    .map((num) => parseInt(num, 10));
  const expirationDate = new Date(year, month - 1, day);
  expirationDate.setDate(expirationDate.getDate() + Number(visaDuration));
  expirationDate.setHours(23, 59, 59, 999);
  return expirationDate;
};

export const calculateInvalidDuration = (expiryDate) => {
  const now = new Date();
  if (now <= expiryDate) return "";
  const timeDiff = now - expiryDate;
  const daysInvalid = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  return daysInvalid > 0 ? `${daysInvalid} days invalid` : "";
};

export const calculateRemainingTime = (expiryDate, country = null) => {
  const now = new Date();

  // If we have country info, we should ideally use the calculation from timezoneUtils
  // but for simplicity and backwards compatibility, we'll keep the logic here
  // and ensure the expiryDate passed in was already calculated correctly.

  const timeDiff = expiryDate - now;
  const totalSeconds = Math.max(Math.floor(timeDiff / 1000), 0);

  const days = Math.floor(totalSeconds / (3600 * 24));
  const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    totalSeconds,
    days,
    hours,
    minutes,
    seconds,
    isValid: timeDiff > 0
  };
};

